import {
  CTA_BUTTON_COPY,
  CTA_BUTTON_COPY_2,
  CTA_BUTTON_COPY_8,
  CTA_BUTTON_COPY_9,
  CTA_LINK_2,
  FORM_IDS,
} from "components/data/landing-page-data";
import FormSectionF from "components/landing-pages/surveillance/version-f/FormSectionF";
import HighlightsF from "components/landing-pages/surveillance/version-f/HighlightsF";
import PlatformF from "components/landing-pages/surveillance/version-f/PlatformF";
import GlobalLayout from "components/page/GlobalLayout";
import { graphql, useStaticQuery } from "gatsby";
import React from "react";
import { Helmet } from "react-helmet";

import bandwidthIcon from "components/landing-pages/icons/bandwidth.svg";
import operationsIcon from "components/landing-pages/icons/operations.svg";
import alertsIcon from "components/landing-pages/icons/alerts.svg";
import timeIcon from "components/landing-pages/icons/time.svg";
import {
  SectionContainer,
  SectionInner,
} from "components/rhombus-UI/theme/containers";
import RhombusButton from "components/common/RhombusButton";
import CaseStudyCard from "components/landing-pages/experiments/CaseStudyCard";
import AiBanner from "components/common/banners/AiBanner";
import IndustryLogos from "components/landing-pages/experiments/IndustryLogos";
import HeroLayout5 from "components/common/hero/HeroLayout5";

const EXPERIMENTAL_FOOTER_MENUS = [
  {
    title: "LEARN MORE",
    links: [
      { title: "Pricing", path: "/lp/pricing/" },
      { title: "Contact", path: "/lp/cta-3/" },
    ],
  },
  {
    title: "PRODUCTS",
    links: [
      { title: "Cameras", path: "/lp/cameras-g/" },
      { title: "Access Control", path: "/lp/access-control-d/" },
    ],
  },
];

const LPRLandingPage = () => {
  const imageData = useStaticQuery(graphql`
    query {
      hero: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/cameras-suite-min.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      logo1: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/logos/pepsico-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, height: 43, width: 168)
        }
      }
      logo2: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/logos/alliance-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, height: 41, width: 150)
        }
      }
      logo3: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/logos/24-hour-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, height: 38, width: 167)
        }
      }
      logo4: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/logos/ups-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, height: 32, width: 193)
        }
      }
      logo5: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/logos/aramark-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, height: 42, width: 148)
        }
      }
      logo6: file(
        relativePath: {
          eq: "components/landing-pages/experiments/assets/logos/laguna-beach-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FIXED, height: 36, width: 177)
        }
      }
      card1: file(
        relativePath: { eq: "components/landing-pages/img/lpr/lpr-card-1.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      card2: file(
        relativePath: { eq: "components/landing-pages/img/lpr/lpr-card-2.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      card3: file(
        relativePath: { eq: "components/landing-pages/img/lpr/lpr-card-3.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
      card4: file(
        relativePath: { eq: "components/landing-pages/img/lpr/lpr-card-4.png" }
      ) {
        childImageSharp {
          gatsbyImageData(layout: FULL_WIDTH)
        }
      }
    }
  `);

  return (
    <GlobalLayout
      landingPage
      useExperimentalFooter
      includeButton
      buttonText={CTA_BUTTON_COPY}
      color="var(--secondary-bg)"
      expirementalFooterMenus={EXPERIMENTAL_FOOTER_MENUS}
    >
      <Helmet>
        <title>License Plate Reader Instant Demo | Rhombus</title>
        <meta
          name="description"
          content="Secures garages, parking lots, and building perimeters with Rhombus."
        />
      </Helmet>
      <HeroLayout5
        formTitle={"See It to Believe It. Request a Demo."}
        buttonText={CTA_BUTTON_COPY}
        title="Advanced License Plate Recognition and Remote Visibility"
        formId={FORM_IDS.LIVE_DEMO}
        blurb="Best-in-class license plate recognition, AI detections, and remote video footage to protect building perimeters and parking lots. LPR cameras - bullets, domes and multisensors - for diverse environments."
        video={{
          src: "https://mediaassets.rhombus.com/LPR.webm",
          playbackRate: 1,
        }}
      />
      <IndustryLogos
        logos={[
          imageData.logo1,
          imageData.logo2,
          imageData.logo3,
          imageData.logo4,
          imageData.logo5,
          imageData.logo6,
        ]}
      />
      <HighlightsF
        stats={[
          { title: "99.9%", subTitle: "Platform Uptime for Unlimited Devices" },
          { title: "120K+", subTitle: "Cameras Deployed Globally" },
          { title: "3M+", subTitle: "Video Events Analyzed Daily" },
        ]}
        iconBoxes={[
          {
            icon: { src: operationsIcon },
            text: "Manually scrubbing through footage to find license plates",
          },
          {
            icon: { src: alertsIcon },

            text: "Delayed alerts due to outdated systems",
          },
          {
            icon: { src: timeIcon },

            text: "Long reviews to match license plates and identify threats",
          },
          {
            icon: { src: bandwidthIcon },

            text: "High bandwidth consumption that overwhelms your network",
          },
        ]}
        title="Security Operations Shouldn't Be Cumbersome"
      />
      <PlatformF
        title="The Rhombus Advantage: Simple and Rapid Security"
        cardBgColor="var(--primary-bg)"
        showTestimonial={false}
        cards={[
          {
            image: imageData.card1,
            title: "Smart Search",
            blurb:
              "Quick investigations with intelligence filtering for vehicles and license plates across cameras and locations.",
          },
          {
            image: imageData.card2,
            title: "Real-Time Alerts",
            blurb:
              "Customize notifications for users and AI detections to streamline security response.",
          },
          {
            image: imageData.card3,
            title: "Reliable, Remote Visibility",
            blurb:
              "24/7 desktop and mobile streaming. PoE cameras with onboard storage and low latency for protection during network issues.",
          },
          {
            image: imageData.card4,
            title: "All-in-One Solution",
            blurb:
              "Set up in minutes and gain unlimited scalability, automatic updates, and industry leading warranties.",
          },
        ]}
      />
      <SectionContainer color="var(--neutral-100)" style={{ paddingTop: 0 }}>
        <SectionInner
          style={{ justifyContent: "center", alignItems: "center" }}
        >
          <RhombusButton
            path={CTA_LINK_2}
            title={CTA_BUTTON_COPY_9}
            type={"primary-dark"}
          />
        </SectionInner>
      </SectionContainer>
      <AiBanner />
      <CaseStudyCard />
      <FormSectionF
        formTitle={CTA_BUTTON_COPY_2}
        formSubmitText={CTA_BUTTON_COPY_8}
        formId={FORM_IDS.GET_QUOTE_ADS}
      />
    </GlobalLayout>
  );
};

export default LPRLandingPage;
